import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryClient';
import {
  acceptFriendRequest,
  denyFriendRequest,
  getFriendship,
  getSentFriendRequest,
  listActivityFeed,
  listFriends,
  listIncomingFriendRequests,
  listMutualFriends,
  listOnlineUserIds,
  listUserCommunities,
  blockUser,
  removeFriend,
  searchProfiles,
  sendFriendRequest,
  type FriendRequest,
} from '../api/social';

/** A user's friends (symmetric `friends` table → resolved profiles). */
export function useFriends(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.friends(userId ?? ''),
    queryFn: () => listFriends(userId!),
    enabled: Boolean(userId),
  });
}

/** Which of the given friend ids are currently online (polled every 30s). */
export function useOnlineFriendIds(ids: string[]) {
  const key = [...ids].sort().join(',');
  return useQuery({
    queryKey: ['presence', 'online', key],
    queryFn: () => listOnlineUserIds(ids),
    enabled: ids.length > 0,
    refetchInterval: 30000,
  });
}

export function useFriendship(userId: string | undefined, otherUserId: string | undefined) {
  return useQuery({
    queryKey: ['friends', 'edge', userId ?? '', otherUserId ?? ''],
    queryFn: () => getFriendship(userId!, otherUserId!),
    enabled: Boolean(userId) && Boolean(otherUserId) && userId !== otherUserId,
  });
}

/** True while the current user has a pending outgoing friend request out. */
export function useSentFriendRequest(userId: string | undefined, otherUserId: string | undefined) {
  return useQuery({
    queryKey: ['friends', 'sent', userId ?? '', otherUserId ?? ''],
    queryFn: () => getSentFriendRequest(userId!, otherUserId!),
    enabled: Boolean(userId) && Boolean(otherUserId) && userId !== otherUserId,
  });
}

export function useMutualFriends(userId: string | undefined, otherUserId: string | undefined) {
  return useQuery({
    queryKey: ['friends', 'mutual', userId ?? '', otherUserId ?? ''],
    queryFn: () => listMutualFriends(userId!, otherUserId!),
    enabled: Boolean(userId) && Boolean(otherUserId) && userId !== otherUserId,
  });
}

export function useAddFriend(userId: string | undefined, otherUserId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => sendFriendRequest(userId!, otherUserId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['friends', 'edge', userId ?? '', otherUserId ?? ''] });
      qc.invalidateQueries({ queryKey: ['friends', 'sent', userId ?? '', otherUserId ?? ''] });
      qc.invalidateQueries({ queryKey: queryKeys.friends(userId ?? '') });
      qc.invalidateQueries({ queryKey: queryKeys.friends(otherUserId ?? '') });
      qc.invalidateQueries({ queryKey: ['friends', 'incoming', otherUserId ?? ''] });
    },
  });
}

/** Search members by handle/display name (inbox add-friends search). */
export function useProfileSearch(query: string, excludeId?: string) {
  return useQuery({
    queryKey: ['profiles', 'search', query.trim(), excludeId ?? ''],
    queryFn: () => searchProfiles(query, excludeId),
    enabled: query.trim().length >= 2,
  });
}

/** Remove a friend (any user id). Invalidates friends + the edge state. */
export function useRemoveFriend(userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (otherUserId: string) => removeFriend(userId!, otherUserId),
    onSuccess: (_data, otherUserId) => {
      qc.invalidateQueries({ queryKey: queryKeys.friends(userId ?? '') });
      qc.invalidateQueries({ queryKey: ['friends', 'edge', userId ?? '', otherUserId] });
    },
  });
}

/** Block a user (also drops the friendship). */
export function useBlockUser(userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (otherUserId: string) => blockUser(userId!, otherUserId),
    onSuccess: (_data, otherUserId) => {
      qc.invalidateQueries({ queryKey: queryKeys.friends(userId ?? '') });
      qc.invalidateQueries({ queryKey: ['friends', 'edge', userId ?? '', otherUserId] });
    },
  });
}

/** Send a friend request to any user id (used by the add-friends search). */
export function useSendFriendRequestTo(userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (otherUserId: string) => sendFriendRequest(userId!, otherUserId),
    onSuccess: (_data, otherUserId) => {
      qc.invalidateQueries({ queryKey: queryKeys.friends(userId ?? '') });
      qc.invalidateQueries({ queryKey: ['friends', 'sent', userId ?? '', otherUserId] });
    },
  });
}

export function useIncomingFriendRequests(userId: string | undefined) {
  return useQuery({
    queryKey: ['friends', 'incoming', userId ?? ''],
    queryFn: () => listIncomingFriendRequests(userId!),
    enabled: Boolean(userId),
    refetchInterval: 30000,
  });
}

export function useFriendRequestActions(userId: string | undefined) {
  const qc = useQueryClient();
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['friends', 'incoming', userId ?? ''] });
    qc.invalidateQueries({ queryKey: queryKeys.friends(userId ?? '') });
  };
  const accept = useMutation({
    mutationFn: (request: FriendRequest) => acceptFriendRequest(request),
    onSuccess: refresh,
  });
  const deny = useMutation({
    mutationFn: (id: string) => denyFriendRequest(id),
    onSuccess: refresh,
  });
  return { accept, deny };
}

/** Community notifications feed (activity from the viewer's communities/friends). */
export function useActivityFeed(userId: string | undefined) {
  return useQuery({
    queryKey: ['activity', userId ?? ''],
    queryFn: () => listActivityFeed(userId!),
    enabled: Boolean(userId),
    refetchInterval: 60000,
  });
}

/** Communities a user belongs to. */
export function useUserCommunities(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.userCommunities(userId ?? ''),
    queryFn: () => listUserCommunities(userId!),
    enabled: Boolean(userId),
  });
}
